export interface GBPMetrics {
  id: string;
  name: string;
  status: 'verified' | 'pending' | 'suspended';
  completeness: number;
  reviews: {
    total: number;
    average: number;
    responded: number;
  };
  posts: {
    planned: number;
    published: number;
    engagement: number;
  };
  photos: {
    total: number;
    views: number;
  };
  insights: {
    views: number;
    searches: number;
    clicks: number;
  };
}

export interface GBPChecklist {
  id: string;
  category: string;
  task: string;
  status: 'completed' | 'pending' | 'in_progress';
  priority: 'high' | 'medium' | 'low';
  notes?: string;
}

export interface GBPPostSuggestion {
  id: string;
  language: 'en' | 'es' | 'pt' | 'fr';
  title: string;
  content: string;
  type: 'event' | 'offer' | 'news' | 'product';
  cta?: {
    text: string;
    url: string;
  };
  image?: string;
}

export interface GBPActivity {
  date: string;
  posts: number;
  reviews: number;
  responses: number;
  views: number;
}

// Mock data pour Le 39V
export const gbpData39V = {
  metrics: {
    id: 'gbp_39v',
    name: 'Le 39V',
    status: 'verified',
    completeness: 75, // Réalité : fiche existante mais sous-optimisée
    reviews: {
      total: 818, // Données réelles Google
      average: 4.5, // Données réelles Google
      responded: 620 // ~75% de taux de réponse
    },
    posts: {
      planned: 12,
      published: 4, // Faible activité
      engagement: 320
    },
    photos: {
      total: 45, // Insuffisant pour un restaurant de ce standing
      views: 8500
    },
    insights: {
      views: 12500,
      searches: 6200,
      clicks: 2800
    }
  } as GBPMetrics,

  checklist: [
    {
      id: 'check_1',
      category: 'Informations',
      task: 'NAP cohérent',
      status: 'completed',
      priority: 'high',
      notes: 'Vérifié sur tous les répertoires'
    },
    {
      id: 'check_2',
      category: 'Photos',
      task: 'Photos récentes',
      status: 'completed',
      priority: 'high'
    },
    {
      id: 'check_3',
      category: 'Catégories',
      task: 'Catégories correctes',
      status: 'completed',
      priority: 'medium'
    },
    {
      id: 'check_4',
      category: 'Q&A',
      task: 'Q&A renseignées',
      status: 'in_progress',
      priority: 'medium',
      notes: '5 questions en attente de réponse'
    },
    {
      id: 'check_5',
      category: 'Menu',
      task: 'Menu à jour',
      status: 'completed',
      priority: 'high'
    },
    {
      id: 'check_6',
      category: 'Posts',
      task: 'Posts réguliers',
      status: 'in_progress',
      priority: 'high',
      notes: 'Planning mensuel en cours'
    }
  ] as GBPChecklist[],

  postSuggestions: [
    {
      id: 'post_en_1',
      language: 'en',
      title: 'Romantic Rooftop Dinner in Paris',
      content: 'Experience an unforgettable evening with panoramic views of Paris. Our chef special tasting menu paired with exceptional wines awaits you.',
      type: 'offer',
      cta: {
        text: 'Book Now',
        url: '/en/reservations'
      },
      image: '/images/rooftop-dinner.jpg'
    },
    {
      id: 'post_es_1',
      language: 'es',
      title: 'Cena con Vista a la Torre Eiffel',
      content: 'Disfrute de una velada mágica con vistas panorámicas de París. Menú degustación especial del chef con maridaje de vinos.',
      type: 'offer',
      cta: {
        text: 'Reservar',
        url: '/es/reservas'
      }
    },
    {
      id: 'post_pt_1',
      language: 'pt',
      title: 'Jantar Romântico com Vista',
      content: 'Uma experiência inesquecível com vista panorâmica de Paris. Menu especial do chef com harmonização de vinhos.',
      type: 'offer',
      cta: {
        text: 'Reservar',
        url: '/pt/reservas'
      }
    }
  ] as GBPPostSuggestion[],

  activity: [
    {
      date: '2024-01',
      posts: 12,
      reviews: 245,
      responses: 238,
      views: 15800
    },
    {
      date: '2024-02',
      posts: 10,
      reviews: 218,
      responses: 215,
      views: 14500
    },
    {
      date: '2024-03',
      posts: 8,
      reviews: 262,
      responses: 255,
      views: 16200
    }
  ] as GBPActivity[]
};

// Mock data pour Café Max - Données réelles
export const gbpDataCafeMax = {
  metrics: {
    id: 'gbp_cafemax',
    name: 'Café Max',
    status: 'verified' as const,
    completeness: 62, // Fiche existante mais sous-optimisée
    reviews: {
      total: 500, // Données réelles Google
      average: 4.6, // Données réelles Google
      responded: 340 // ~68% de taux de réponse (à améliorer)
    },
    posts: {
      planned: 24,
      published: 8, // Activité faible - besoin de stratégie
      engagement: 180
    },
    photos: {
      total: 45, // Insuffisant pour un restaurant
      views: 4200
    },
    insights: {
      views: 5800,
      searches: 2400,
      clicks: 980
    }
  } as GBPMetrics,

  checklist: [
    {
      id: 'check_cm_1',
      category: 'Informations',
      task: 'NAP cohérent',
      status: 'completed' as const,
      priority: 'high' as const,
      notes: 'Vérifié sur Google et principaux annuaires'
    },
    {
      id: 'check_cm_2',
      category: 'Photos',
      task: 'Photos professionnelles récentes',
      status: 'in_progress' as const,
      priority: 'high' as const,
      notes: 'Seulement 45 photos - besoin de 50 nouvelles'
    },
    {
      id: 'check_cm_3',
      category: 'Catégories',
      task: 'Catégories correctes',
      status: 'completed' as const,
      priority: 'medium' as const,
      notes: 'Bistrot, Restaurant français, Restaurant traditionnel'
    },
    {
      id: 'check_cm_4',
      category: 'Q&A',
      task: 'Questions & Réponses renseignées',
      status: 'pending' as const,
      priority: 'high' as const,
      notes: 'Section Q&A non renseignée - 0 question proactive'
    },
    {
      id: 'check_cm_5',
      category: 'Menu',
      task: 'Menu à jour',
      status: 'completed' as const,
      priority: 'high' as const,
      notes: 'Menu PDF disponible mais pas optimisé SEO'
    },
    {
      id: 'check_cm_6',
      category: 'Posts',
      task: 'Posts GMB réguliers',
      status: 'pending' as const,
      priority: 'high' as const,
      notes: 'Seulement 8 posts publiés - besoin de 3/semaine'
    },
    {
      id: 'check_cm_7',
      category: 'Attributs',
      task: 'Attributs complétés',
      status: 'pending' as const,
      priority: 'medium' as const,
      notes: 'Manque : WiFi, terrasse, PMR, parking'
    },
    {
      id: 'check_cm_8',
      category: 'Horaires',
      task: 'Horaires spéciaux',
      status: 'completed' as const,
      priority: 'medium' as const,
      notes: 'Fermé samedi-dimanche bien indiqué'
    },
    {
      id: 'check_cm_9',
      category: 'Avis',
      task: 'Réponses aux avis',
      status: 'in_progress' as const,
      priority: 'high' as const,
      notes: 'Taux de réponse 68% - objectif 90%+'
    }
  ] as GBPChecklist[],

  postSuggestions: [
    // Posts FR
    {
      id: 'post_cm_fr_1',
      language: 'fr',
      title: 'Histoire du Café Max - Haut lieu de la Résistance',
      content: 'Découvrez l\'histoire fascinante de notre bistrot, ancien repaire de résistants durant la Seconde Guerre mondiale. Lieu de rencontre clandestin, rebaptisé "Café Max" en hommage à Jean Moulin.',
      type: 'update',
      cta: {
        text: 'En savoir plus',
        url: '/fr/histoire'
      }
    },
    {
      id: 'post_cm_fr_2',
      language: 'fr',
      title: 'Plat du Jour - Confit de Canard Maison',
      content: 'Notre chef Frédéric Vardon vous propose aujourd\'hui un confit de canard traditionnel, pommes sarladaises et haricots verts. Réservez votre table dès maintenant !',
      type: 'offer',
      cta: {
        text: 'Réserver',
        url: '/fr/reserver'
      }
    },
    {
      id: 'post_cm_fr_3',
      language: 'fr',
      title: 'Terrasse Ensoleillée Disponible',
      content: 'Profitez de notre terrasse en plein cœur du 7ème arrondissement. Idéal pour un déjeuner d\'affaires ou un moment convivial entre amis.',
      type: 'update',
      cta: {
        text: 'Voir les photos',
        url: '/fr/galerie'
      }
    },
    {
      id: 'post_cm_fr_4',
      language: 'fr',
      title: 'Menu Déjeuner - Formule du Midi',
      content: 'Entrée + Plat + Dessert à 24€. Cuisine française traditionnelle avec produits frais de saison. Du mardi au vendredi, de 12h à 14h30.',
      type: 'offer',
      cta: {
        text: 'Découvrir le menu',
        url: '/fr/menu'
      }
    },
    {
      id: 'post_cm_fr_5',
      language: 'fr',
      title: 'Chef Frédéric Vardon - Cuisine de Saison',
      content: 'Le chef Frédéric Vardon, également propriétaire du 39V, vous propose une cuisine canaille et traditionnelle française, avec des produits frais du marché.',
      type: 'update',
      cta: {
        text: 'Rencontrer le chef',
        url: '/fr/le-chef'
      }
    },
    // Posts EN
    {
      id: 'post_cm_en_1',
      language: 'en',
      title: 'Historic French Bistro - Resistance Heritage',
      content: 'Discover the fascinating history of Café Max, a former secret meeting place for French Resistance fighters during WWII. Named after Jean Moulin\'s resistance code name "Max".',
      type: 'update',
      cta: {
        text: 'Learn more',
        url: '/en/history'
      }
    },
    {
      id: 'post_cm_en_2',
      language: 'en',
      title: 'Traditional French Cuisine by Chef Vardon',
      content: 'Chef Frédéric Vardon offers authentic French bistro cuisine with seasonal products. Daily specials and classic Parisian dishes.',
      type: 'update',
      cta: {
        text: 'Book a table',
        url: '/en/reservations'
      }
    },
    {
      id: 'post_cm_en_3',
      language: 'en',
      title: 'Sunny Terrace in the 7th Arrondissement',
      content: 'Enjoy our lovely terrace near Les Invalides and the Eiffel Tower. Perfect for business lunch or a relaxing meal with friends.',
      type: 'update',
      cta: {
        text: 'View photos',
        url: '/en/gallery'
      }
    },
    {
      id: 'post_cm_en_4',
      language: 'en',
      title: 'Lunch Special - 3-Course Menu €24',
      content: 'Starter + Main + Dessert for only €24. Traditional French cuisine with fresh seasonal ingredients. Tuesday to Friday, 12pm-2:30pm.',
      type: 'offer',
      cta: {
        text: 'See the menu',
        url: '/en/menu'
      }
    }
  ] as GBPPostSuggestion[],

  activity: [
    {
      date: '2025-09',
      posts: 2,
      reviews: 38,
      responses: 24,
      views: 4200
    },
    {
      date: '2025-10',
      posts: 3,
      reviews: 42,
      responses: 29,
      views: 4800
    },
    {
      date: '2025-11',
      posts: 3,
      reviews: 45,
      responses: 32,
      views: 5800
    }
  ] as GBPActivity[]
};

export const getGBPData = (restaurantId: string) => {
  return restaurantId === '39v' ? gbpData39V : gbpDataCafeMax;
};
